import { useState } from "react";
import dayjs from "dayjs";

import { useAdminMissingFiles } from "@/hooks/queries/useAdminMissingFiles";
import type { ContractingMissingPriceRelationReport } from "@/types/domain";
import {
  EMPTY_ERROR_FILTERS,
  type ErrorFiltersValue,
} from "@/components/admin/ContractingErrorFilters";
import {
  ContractingReportTable,
  type ReportColumn,
} from "@/components/admin/ContractingReportTable";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const COLUMNS: ReportColumn<ContractingMissingPriceRelationReport>[] = [
  {
    key: "missing",
    header: "Falta",
    render: () => (
      <span className="text-sm text-muted-foreground">Relación de precios</span>
    ),
  },
  {
    key: "failCount",
    header: "Intentos",
    render: (row) =>
      row.discarded ? (
        <span
          className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive"
          title="El scraper la descartó: no la vuelve a buscar"
        >
          {row.failCount} · descartada
        </span>
      ) : (
        <span className="text-sm tabular-nums text-muted-foreground">
          {row.failCount}
        </span>
      ),
  },
];

export default function AdminMissingFiles() {
  const [filters, setFilters] =
    useState<ErrorFiltersValue>(EMPTY_ERROR_FILTERS);
  const [onlyDiscarded, setOnlyDiscarded] = useState(false);
  const { data = [], isLoading } = useAdminMissingFiles({
    stages: filters.stages,
    publicatedFrom: filters.publicatedFrom || undefined,
    // Fin del día para que el rango incluya la fecha elegida.
    publicatedTo: filters.publicatedTo
      ? dayjs(filters.publicatedTo).endOf("day").toISOString()
      : undefined,
    sortBy: filters.sortBy,
    onlyDiscarded,
  });

  return (
    <ContractingReportTable
      title="Documentos faltantes"
      description="Obras sin relación de precios: en cola del scraper o descartadas tras 3 intentos."
      filters={filters}
      onFiltersChange={setFilters}
      extraFilters={
        <label className="flex cursor-pointer items-center gap-2 pb-2.5">
          <Checkbox
            checked={onlyDiscarded}
            onCheckedChange={(checked) => setOnlyDiscarded(checked === true)}
          />
          <Label className="cursor-pointer text-sm font-normal">
            Solo descartadas
          </Label>
        </label>
      }
      extraFiltersActive={onlyDiscarded}
      rows={data}
      columns={COLUMNS}
      isLoading={isLoading}
      emptyMessage="No hay obras sin relación de precios."
    />
  );
}
